if (typeof DosageCalculatorOneBottle !== "function") {
  class DosageCalculatorOneBottle extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        selected: "is-selected",
      };

      this.selectors = {
        sizeWrapper: ".dosage-calculator-one-bottle__size",
        inputRadios: 'input[type="radio"]',
        selectedRadio: 'input[type="radio"]:checked',
        resultDays: "[data-result-days]",
        resultDose: "[data-result-dose]",
        resultServings: "[data-result-servings]",
      };

      this.elements = {
        sizeWrappers: this.querySelectorAll(this.selectors.sizeWrapper),
        inputRadios: this.querySelectorAll(this.selectors.inputRadios),
        resultDays: this.querySelector(this.selectors.resultDays),
        resultDose: this.querySelector(this.selectors.resultDose),
        resultServings: this.querySelector(this.selectors.resultServings),
      };

      // "80 days" -> 80  (same parsing as product-custom-siblings.js)
      this.baseDays =
        parseInt((this.dataset.baseDays || "").match(/\d+/)?.[0], 10) || 0;
      this.doseUnit = (this.dataset.doseUnit || "").trim().toLowerCase();
      this.storageKey = "selectedDogWeight"; // shared with product-custom-siblings.js

      this.restoreSelection();
      this.addListeners();
      this.render();
    }

    addListeners() {
      this.elements.inputRadios.forEach((radio) => {
        radio.addEventListener("change", () => {
          this.saveSelection(radio);
          this.render();
        });
      });
    }

    /**
     * Pre-select the range matching the dog weight the customer already
     * chose elsewhere on the page (siblings block stores the range middle).
     */
    restoreSelection() {
      let storedWeight = null;
      try {
        storedWeight = parseFloat(localStorage.getItem(this.storageKey));
      } catch (e) {
        return;
      }
      if (!storedWeight) return;

      const match = [...this.elements.inputRadios].find((radio) => {
        const start = parseFloat(radio.dataset.start);
        const end = parseFloat(radio.dataset.end);
        return storedWeight >= start && storedWeight <= end;
      });

      if (match) match.checked = true;
    }

    saveSelection(radio) {
      const start = parseFloat(radio.dataset.start);
      const end = parseFloat(radio.dataset.end);
      try {
        localStorage.setItem(this.storageKey, ((start + end) / 2).toString());
      } catch (e) {
        /* storage unavailable – ignore */
      }
    }

    render() {
      const selected = this.querySelector(this.selectors.selectedRadio);

      this.elements.sizeWrappers.forEach((wrapper) => {
        const isSelected = wrapper.contains(selected);
        wrapper.classList.toggle(this.classes.selected, isSelected);
      });

      const dose = parseFloat(selected?.value);

      if (!selected || !dose || !this.baseDays) {
        this.setText(this.elements.resultDays, "–");
        this.setText(this.elements.resultDose, "–");
        this.setText(this.elements.resultServings, "–");
        return;
      }

      const days = Math.round(this.baseDays / dose);

      this.setText(this.elements.resultDays, days);
      this.setText(
        this.elements.resultDose,
        `${this.formatFraction(dose)} ${this.formatUnit(dose)}`.trim(),
      );
      this.setText(this.elements.resultServings, days);
    }

    setText(element, value) {
      if (element) element.textContent = value;
    }

    /**
     * 0.5 -> "½", 1.5 -> "1½", 0.33 -> "⅓", 2 -> "2", 0.4 -> "0.4"
     */
    formatFraction(number) {
      const fractions = [
        [0.125, "⅛"],
        [0.25, "¼"],
        [0.333, "⅓"],
        [0.375, "⅜"],
        [0.5, "½"],
        [0.625, "⅝"],
        [0.666, "⅔"],
        [0.75, "¾"],
        [0.875, "⅞"],
      ];

      const whole = Math.floor(number);
      const remainder = number - whole;

      if (remainder < 0.01) return `${whole}`;

      const match = fractions.find(
        ([value]) => Math.abs(value - remainder) < 0.02,
      );
      if (match) return `${whole > 0 ? whole : ""}${match[1]}`;

      return `${parseFloat(number.toFixed(2))}`;
    }

    formatUnit(dose) {
      const abbreviations = {
        teaspoon: "tsp",
        teaspoons: "tsp",
        tablespoon: "tbsp",
        tablespoons: "tbsp",
      };

      if (abbreviations[this.doseUnit]) return abbreviations[this.doseUnit];
      if (!this.doseUnit) return "";

      // simple pluralisation for units like "capsule" / "scoop"
      if (dose > 1 && !this.doseUnit.endsWith("s")) return `${this.doseUnit}s`;
      return this.doseUnit;
    }
  }

  if (!customElements.get("dosage-calculator-one-bottle")) {
    customElements.define(
      "dosage-calculator-one-bottle",
      DosageCalculatorOneBottle,
    );
  }
}
